import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { api, downloadFile } from "@/lib/api";
import { PageTitle, Panel, Btn } from "@/admin/ui";
import { BarChart, HBars } from "@/admin/Charts";

function SalesCharts({ a }) {
  const currencies = [...new Set(a.daily.flatMap((d) => Object.keys(d.revenue)))];
  const promoRows = Object.entries(a.promo_usage).map(([k, v]) => ({ key: k, value: v.paid, text: `${v.paid} paid / ${v.orders} orders · ${Object.entries(v.discount).map(([c, x]) => `${c} ${x}`).join(", ") || "no discount yet"}` }));
  const revRows = Object.entries(a.revenue).map(([c, v]) => ({ key: c, value: v, text: `${c} ${Number(v).toFixed(2)}` }));
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-2" data-testid="sales-charts">
      <Panel><BarChart data={a.daily} valueKey="paid" label="Daily paid orders — last 30 days" testId="chart-daily-paid" /></Panel>
      <Panel><BarChart data={a.daily} valueKey="orders" label="Daily orders placed — last 30 days" testId="chart-daily-orders" color="hsl(218 11% 45%)" /></Panel>
      {currencies.map((c) => (
        <Panel key={c}><BarChart data={a.daily.map((d) => ({ day: d.day, v: d.revenue[c] || 0 }))} valueKey="v" label={`Daily revenue (${c})`} testId={`chart-revenue-${c}`} color="hsl(152 45% 45%)" /></Panel>
      ))}
      <Panel><HBars rows={revRows} label="Total revenue by currency" testId="chart-revenue-currency" /></Panel>
      <Panel><HBars rows={promoRows} label="Promo code usage" testId="chart-promo-usage" /></Panel>
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div className="rounded-md border border-border bg-card p-5" data-testid={testId}>
    <p className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
    <p className="mt-2 font-serif-display text-3xl">{value}</p>
  </div>
);

const STAGES = [
  ["1. Planning & database understanding", "Done — documents and logo imported"],
  ["2. Prototype & interpretation engine", "Built — v1 published (first-vowel A); letter rules being drafted"],
  ["3. Report generation (AI narrative + PDF)", "Built — review sample reports"],
  ["4. Payments (Razorpay / PayPal test mode) & email", "Built in test mode"],
  ["5. Deployment & website integration", "Not started — requires your approval"],
];

export default function Overview() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return null;
  const rev = Object.entries(a.revenue).map(([c, v]) => `${c} ${v}`).join(" · ") || "—";
  return (
    <div>
      <PageTitle title="Overview" sub="Business activity and interpretation-database status.">
        <Btn variant="ghost" onClick={() => downloadFile("/admin/export", "namonics_export.json")} data-testid="export-database-btn"><Download className="h-4 w-4" /> Export database</Btn>
      </PageTitle>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Visitors" value={a.visitors} testId="stat-visitors" />
        <Stat label="Orders placed" value={a.orders} testId="stat-orders" />
        <Stat label="Successful payments" value={a.paid} testId="stat-paid" />
        <Stat label="Conversion" value={`${a.conversion_rate}%`} testId="stat-conversion" />
        <Stat label="Revenue" value={rev} testId="stat-revenue" />
        <Stat label="Report failures" value={a.report_failures} testId="stat-report-failures" />
        <Stat label="Email failures" value={a.email_failures} testId="stat-email-failures" />
        <Stat label="Golden tests" value={a.golden_tests} testId="stat-golden" />
      </div>
      <SalesCharts a={a} />
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Panel title="Interpretation database" testId="db-status-panel">
          <p className="text-sm">{a.documents} source documents · {a.passages} extracted passages</p>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
            {Object.entries(a.rules).map(([k, v]) => <span key={k}>{k}: <strong className="text-foreground">{v}</strong></span>)}
          </div>
          {!!Object.keys(a.by_country).length && <p className="mt-4 text-sm text-muted-foreground">Sales by country: {Object.entries(a.by_country).map(([k, v]) => `${k} ${v}`).join(", ")}</p>}
        </Panel>
        <Panel title="Development stages" testId="stages-panel">
          <ul className="space-y-3 text-sm">
            {STAGES.map(([t, d]) => <li key={t}><p>{t}</p><p className="text-xs text-muted-foreground">{d}</p></li>)}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
